// notify.js — thin wrapper around the browser Notification API used to
// deliver OS-level system notifications (with sound) when a new MICM event
// arrives. Works while the browser process is running, even if the MICM
// tab is minimized / not focused / running behind other apps.
//
// True WhatsApp-style push (delivered even when browser is closed) requires
// Web Push + VAPID keys — that's a separate follow-up.

const PERMISSION_ASK_KEY = "micm_notif_ask_snoozed_at";
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000; // 3 days

export function isSupported() {
    return typeof window !== "undefined" && "Notification" in window;
}

export function permission() {
    return isSupported() ? Notification.permission : "unsupported";
}

/**
 * Request permission — must be called from within a user gesture on
 * mobile browsers (button click). Returns the resolved permission string.
 */
export async function ensurePermission() {
    if (!isSupported()) return "unsupported";
    if (Notification.permission === "granted") return "granted";
    if (Notification.permission === "denied") return "denied";
    try {
        const result = await Notification.requestPermission();
        return result;
    } catch (_e) {
        return "denied";
    }
}

export function snoozeAsk() {
    localStorage.setItem(PERMISSION_ASK_KEY, String(Date.now()));
}

export function shouldOfferPrompt() {
    if (!isSupported()) return false;
    if (Notification.permission !== "default") return false;
    const snoozedAt = Number(localStorage.getItem(PERMISSION_ASK_KEY) || 0);
    return !snoozedAt || Date.now() - snoozedAt > SNOOZE_MS;
}

/**
 * Fire a real OS notification. Uses the service-worker registration when
 * available (so the notification survives even if the tab is gone but the
 * SW is still alive), otherwise falls back to the plain window Notification.
 *
 * Options:
 *   title, body, tag (dedupe key), icon, url (where to focus on click),
 *   silent (default false — plays the OS default sound).
 */
export async function showOS(title, opts = {}) {
    if (!isSupported() || Notification.permission !== "granted") return false;
    const payload = {
        body: opts.body || "",
        tag: opts.tag || "micm",
        renotify: opts.renotify !== false,
        icon: opts.icon || "/logo192.png",
        badge: "/logo192.png",
        silent: opts.silent === true, // default: NOT silent, so OS plays sound
        data: { url: opts.url || "/" },
        requireInteraction: opts.requireInteraction === true,
    };
    try {
        if (navigator.serviceWorker && navigator.serviceWorker.ready) {
            const reg = await navigator.serviceWorker.ready;
            if (reg && reg.showNotification) {
                await reg.showNotification(title, payload);
                return true;
            }
        }
    } catch (_e) { /* fall through to window Notification */ }
    try {
        const n = new Notification(title, payload);
        n.onclick = () => {
            window.focus();
            if (opts.url) window.location.href = opts.url;
            n.close();
        };
        return true;
    } catch (_e) {
        return false;
    }
}
